import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  ArrowRight,
  Rocket,
  Building,
  Clock,
  Users,
  Bot,
  Settings,
  Shield,
  Check,
  Star
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { animations } from '@/lib/animations';
import { ROUTES } from '@/lib/constants';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/lib/translations';
import { motion } from 'framer-motion';
import { AnimatedCard, AnimatedButton, AnimatedText } from '@/components/ui/animated-card';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const PricingSection = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language].solutions;

  const features: Feature[] = [
    {
      icon: <Clock className="w-6 h-6 text-primary" />,
      title: t.features.items.customerService.title,
      description: t.features.items.customerService.description
    },
    {
      icon: <Bot className="w-6 h-6 text-primary" />,
      title: t.features.items.sentiment.title,
      description: t.features.items.sentiment.description
    },
    {
      icon: <Settings className="w-6 h-6 text-primary" />,
      title: t.features.items.reservations.title,
      description: t.features.items.reservations.description
    },
    {
      icon: <Shield className="w-6 h-6 text-primary" />,
      title: t.features.items.multilingual.title,
      description: t.features.items.multilingual.description
    }
  ];

  return (
    <section className={`container mx-auto px-4 py-16 ${animations.fadeIn}`}>
      <div className="max-w-4xl mx-auto text-center mb-16">
        <h2 className={`text-3xl font-bold mb-6 ${animations.fadeInSlideUp}`}>
          {t.hero.title}
        </h2>
        <p className={`text-xl text-muted-foreground mb-8 ${animations.fadeInSlideUp} ${animations.delay200}`}>
          {t.hero.subtitle}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
        <Card className={`p-8 ${animations.fadeInSlideUp}`}>
          <div className="flex items-center gap-4 mb-6">
            <div className={`p-3 rounded-full bg-primary/10 ${animations.float}`}>
              <Rocket className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h3 className="text-2xl font-bold">{t.starter.title}</h3>
              <p className="text-muted-foreground">{t.starter.subtitle}</p>
            </div>
          </div>
          <ul className="space-y-4 mb-8">
            {t.starter.features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary mt-1" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          <Button 
            className={`w-full ${animations.buttonHover}`}
            onClick={() => navigate(ROUTES.START_POC)}
          >
            {t.hero.startPOC} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Card>

        <Card className={`p-8 ${animations.fadeInSlideUp} ${animations.delay200}`}>
          <div className="flex items-center gap-4 mb-6">
            <div className={`p-3 rounded-full bg-primary/10 ${animations.float}`}>
              <Building className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h3 className="text-2xl font-bold">{t.enterprise.title}</h3>
              <p className="text-muted-foreground">{t.enterprise.subtitle}</p>
            </div>
          </div>
          <ul className="space-y-4 mb-8">
            {t.enterprise.features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary mt-1" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          <Button 
            className={`w-full ${animations.buttonHover}`}
            onClick={() => navigate(ROUTES.CONTACT_SALES)}
          >
            {t.hero.contactSales} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {features.map((feature, index) => (
          <Card 
            key={index}
            className={`p-6 ${animations.fadeInSlideUp}`}
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div className={`mb-4 ${animations.float}`}>
              {feature.icon}
            </div>
            <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
            <p className="text-sm text-muted-foreground">{feature.description}</p>
          </Card>
        ))}
      </div>
    </section>
  );
};

export default PricingSection; 